import React from 'react';
import { Link } from 'react-router-dom';
import WhyChooseCard from './WhyChooseCard';

interface ReviewPlatform {
  name: string;
  rating: number;
  reviewCount: string;
  stars: number;
  color: string;
}

const SocialProofBar: React.FC = () => {
  const platforms: ReviewPlatform[] = [
    {
      name: 'Google',
      rating: 4.8,
      reviewCount: '360+',
      stars: 4.8,
      color: 'text-blue-600'
    },
    {
      name: 'HomeAdvisor',
      rating: 4.7,
      reviewCount: '90+',
      stars: 4.7,
      color: 'text-orange-600'
    },
    {
      name: 'BirdEye',
      rating: 5.0,
      reviewCount: '363+',
      stars: 5.0,
      color: 'text-green-600'
    }
  ];

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - Math.ceil(rating);

    return (
      <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
        {[...Array(fullStars)].map((_, i) => (
          <svg
            key={`full-${i}`}
            className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 fill-current"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        {hasHalfStar && (
          <svg
            className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 fill-current"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="half-gradient">
                <stop offset="50%" stopColor="currentColor" />
                <stop offset="50%" stopColor="#e5e7eb" />
              </linearGradient>
            </defs>
            <path
              fill="url(#half-gradient)"
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
            />
          </svg>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <svg
            key={`empty-${i}`}
            className="w-4 h-4 md:w-5 md:h-5 text-gray-300 fill-current"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 to-blue-950 py-8 md:py-12">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Main Headline */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Trusted by Over 7,000+ Coloradans
          </h2>
          <p className="text-blue-200 text-xl md:text-2xl font-semibold max-w-3xl mx-auto leading-relaxed">
            Here are just some of the many review sources showing 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-400"> real customer reviews</span>
          </p>
        </div>

        {/* Review Platforms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto mb-8">
          {platforms.map((platform, index) => {
            // Define background images and links for each platform
            const backgroundImages = [
              '/colorado-plumbing-uploads/team/Codee-Jessen-Master-Plumber-17-Years-Experience.jpg', // Google - Professional credentials
              '/colorado-plumbing-uploads/existing-projects/tankless-water-heater-professional-install-highlands-ranch.png', // HomeAdvisor - Quality installation
              '/colorado-plumbing-uploads/locations/westminster/bradford-white-small-closet-professional-install-westminster.JPEG' // BirdEye - Finished work
            ];

            const platformLinks = [
              'https://maps.app.goo.gl/1Z19Rb8D6pa5EqWQ6', // Google Reviews
              'https://www.homeadvisor.com/rated.CFPPlumbingLLC.63409840.html', // HomeAdvisor
              'https://birdeye.com/colorado-first-plumbing-and-sewer-llc-172384402926614' // BirdEye
            ];

            const overlayColors = [
              'from-blue-600/50 to-blue-800/50', // Google - Blue theme
              'from-orange-600/50 to-orange-800/50', // HomeAdvisor - Orange theme  
              'from-green-600/50 to-green-800/50' // BirdEye - Green theme
            ];

            return (
              <a
                key={platform.name}
                href={platformLinks[index]}
                target="_blank"
                rel="noopener noreferrer"
                className="relative rounded-xl shadow-lg p-6 text-center transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-blue-300 overflow-hidden group cursor-pointer"
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: `url(${backgroundImages[index]})`,
                    filter: 'brightness(0.7)'
                  }}
                />
                
                {/* Colored Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${overlayColors[index]}`} />
                
                {/* Content */}
                <div className="relative z-10">
                  {/* Platform Name */}
                  <div className="text-lg md:text-xl font-bold mb-2 text-white">
                    {platform.name}
                  </div>

                  {/* Star Rating */}
                  <div className="flex justify-center mb-2">
                    {renderStars(platform.stars)}
                  </div>

                  {/* Rating Number */}
                  <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                    {platform.rating}
                  </div>

                  {/* Review Count */}
                  <div className="text-sm md:text-base text-blue-100">
                    {platform.reviewCount} Reviews
                  </div>

                  {/* Trust Badge */}
                  <div className="mt-3 inline-flex items-center text-xs text-green-200 font-semibold">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Verified
                  </div>

                  {/* Click indicator */}
                  <div className="mt-2 text-xs text-blue-200 opacity-75">
                    Click to view reviews →
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Link
            to="/reviews"
            className="inline-flex items-center px-6 md:px-8 py-3 md:py-4 bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:bg-blue-700 transform transition-all duration-300 hover:scale-105 text-sm md:text-base"
          >
            Read Our Reviews
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>

        {/* Why Choose Colorado First Card */}
        <div className="mt-8">
          <WhyChooseCard />
        </div>
      </div>
    </section>
  );
};

export default SocialProofBar;